import React, { useCallback } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import SubjectIcon from "@mui/icons-material/Subject";
import NumbersIcon from "@mui/icons-material/Numbers";
import ArrowDropDownCircleIcon from "@mui/icons-material/ArrowDropDownCircle";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../framework/customStore";
import { setConfirmation } from "../../../models/store/storeActions";
import { ConfirmationDialogType } from "../../../models/store/storeTypes";
import { EditorField, FormFieldType } from "./types";
import { SortableFieldList } from "./sortableFieldList";
import { generateFieldId } from "./schemaBuilder";

interface EditorPanelProps {
    fields: EditorField[];
    onFieldsChange: (fields: EditorField[]) => void;
    validationErrors: Map<string, string>;
}

const fieldTypeDefaults: Record<
    FormFieldType,
    { icon: React.ReactNode; translationKey: string }
> = {
    approveReject: {
        icon: <RadioButtonCheckedIcon fontSize="small" />,
        translationKey: "settings.formEditor.approveReject",
    },
    freeText: {
        icon: <TextFieldsIcon fontSize="small" />,
        translationKey: "settings.formEditor.freeText",
    },
    multiLineText: {
        icon: <SubjectIcon fontSize="small" />,
        translationKey: "settings.formEditor.multiLineText",
    },
    number: {
        icon: <NumbersIcon fontSize="small" />,
        translationKey: "settings.formEditor.number",
    },
    checkbox: {
        icon: <CheckBoxIcon fontSize="small" />,
        translationKey: "settings.formEditor.checkbox",
    },
    dropdown: {
        icon: <ArrowDropDownCircleIcon fontSize="small" />,
        translationKey: "settings.formEditor.dropdown",
    },
    attachment: {
        icon: <AttachFileIcon fontSize="small" />,
        translationKey: "settings.formEditor.attachment",
    },
};

export const EditorPanel = ({
    fields,
    onFieldsChange,
    validationErrors,
}: EditorPanelProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const handleAddField = useCallback(
        (type: FormFieldType) => {
            const newField: EditorField = {
                id: generateFieldId(),
                type,
                label: t(fieldTypeDefaults[type].translationKey),
                required: false,
                ...(type === "dropdown" || type === "approveReject"
                    ? { options: ["", ""] }
                    : {}),
                ...(type === "attachment"
                    ? {
                        maxFiles: 5,
                        maxFileSize: 10,
                        acceptedFileTypes: [
                            "image/png",
                            "image/jpeg",
                            "application/pdf",
                        ],
                    }
                    : {}),
            };
            onFieldsChange([...fields, newField]);
        },
        [fields, onFieldsChange, t]
    );

    const handleUpdateField = useCallback(
        (id: string, updates: Partial<EditorField>) => {
            onFieldsChange(
                fields.map((f) => (f.id === id ? { ...f, ...updates } : f))
            );
        },
        [fields, onFieldsChange]
    );

    const handleDeleteField = useCallback(
        (id: string) => {
            const field = fields.find((f) => f.id === id);
            dispatch(
                setConfirmation(
                    ConfirmationDialogType.Warning,
                    t("settings.formEditor.confirmDeleteField", {
                        label: field?.label || "",
                    }),
                    null,
                    () => onFieldsChange(fields.filter((f) => f.id !== id)),
                    () => null,
                    undefined
                )
            );
        },
        [fields, onFieldsChange, dispatch, t]
    );

    const handleDuplicateField = useCallback(
        (id: string) => {
            const index = fields.findIndex((f) => f.id === id);
            if (index === -1) return;
            const original = fields[index];
            const duplicate: EditorField = {
                ...original,
                id: generateFieldId(),
            };
            const updated = [...fields];
            updated.splice(index + 1, 0, duplicate);
            onFieldsChange(updated);
        },
        [fields, onFieldsChange]
    );

    return (
        <Box sx={{ p: 2, height: "100%", overflow: "auto" }}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 2,
                }}
            >
                <Typography variant="h6">
                    {t("settings.formEditor.editor")}
                </Typography>
                <Chip
                    label={t("settings.formEditor.fieldCount", {
                        count: fields.length,
                    })}
                    size="small"
                    variant="outlined"
                />
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                {(
                    Object.entries(fieldTypeDefaults) as [
                        FormFieldType,
                        (typeof fieldTypeDefaults)[FormFieldType],
                    ][]
                ).map(([type, config]) => (
                    <Button
                        key={type}
                        variant="outlined"
                        size="small"
                        startIcon={config.icon}
                        onClick={() => handleAddField(type)}
                        data-testid={`form-editor-add-${type}`}
                    >
                        {t(config.translationKey)}
                    </Button>
                ))}
            </Box>
            <SortableFieldList
                fields={fields}
                onFieldsChange={onFieldsChange}
                onUpdateField={handleUpdateField}
                onDeleteField={handleDeleteField}
                onDuplicateField={handleDuplicateField}
                validationErrors={validationErrors}
            />
        </Box>
    );
};
